import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import EditableHeaderTitle from '../../common/EditableHeaderTitle';

/**
 * PageHeader 组件属性
 * 用于脚本生成页面的顶部导航栏
 */
interface PageHeaderProps {
  /** 剧集名称 */
  episodeName: string;
  /** 保存剧集名称 */
  onEpisodeNameSave?: (title: string) => Promise<void> | void;
  /** 标题是否正在保存 */
  isEpisodeNameSaving?: boolean;
  /** 当前激活的 Tab */
  activeTab: 'script' | 'editor';
  /** 返回按钮点击回调 */
  onBack: () => void;
  /** Tab 切换回调 */
  onTabChange: (tab: 'script' | 'editor') => void;
  /** 是否禁用画面编辑 Tab（未生成分镜时禁用） */
  editorDisabled?: boolean;
}

/**
 * PageHeader 组件
 * 
 * 实现顶部导航栏，包含：
 * - 返回按钮：导航回项目剧集列表
 * - 剧集名称：显示当前编辑的剧集
 * - Tab 切换：在"脚本策划"和"画面编辑"之间切换
 * 
 * 设计参考：Pencil 设计中的 TopHeader 节点
 * - height: 56px
 * - padding: [0, 24]
 * - 底部边框: #1F1F23
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */
export default function PageHeader({
  episodeName,
  onEpisodeNameSave,
  isEpisodeNameSaving = false,
  activeTab,
  onBack,
  onTabChange,
  editorDisabled = false,
}: PageHeaderProps) {
  return (
    <header
      className="h-14 flex items-center justify-between px-6 border-b border-border-subtle flex-shrink-0 bg-bg-surface"
    >
      {/* HeaderLeft: 返回按钮 + 剧集名称 */}
      <div className="flex items-center gap-3">
        {/* BackButton */}
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-gray-50 hover:bg-bg-elevated rounded-lg transition-colors flex-shrink-0 cursor-pointer"
          aria-label="返回"
        >
          <ArrowLeftIcon className="w-[18px] h-[18px]" />
        </button>

        {/* Episode Name */}
        <EditableHeaderTitle
          title={episodeName}
          fallbackTitle="未命名剧集"
          onSave={onEpisodeNameSave}
          isSaving={isEpisodeNameSaving}
          titleClassName="text-[15px] font-semibold text-gray-50"
        />
      </div>

      {/* HeaderTabs: Tab 切换按钮组 */}
      <div className="flex items-center gap-1 p-1 bg-bg-elevated rounded-lg">
        {/* Tab 1: 脚本策划 */}
        <button
          onClick={() => onTabChange('script')}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${activeTab === 'script'
            ? 'bg-primary/10 text-primary'
            : 'text-gray-400 hover:text-gray-200'
            }`}
        >
          脚本策划
        </button>

        {/* Tab 2: 画面编辑 */}
        <button
          onClick={() => !editorDisabled && onTabChange('editor')}
          disabled={editorDisabled}
          title={editorDisabled ? '请先生成分镜' : undefined}
          className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${editorDisabled
            ? 'text-gray-500 cursor-not-allowed'
            : activeTab === 'editor'
              ? 'bg-primary/10 text-primary cursor-pointer'
              : 'text-gray-400 hover:text-gray-200 cursor-pointer'
            }`}
        >
          画面编辑
        </button>
      </div>

      {/* HeaderRight: 预留空间，用于未来扩展 */}
      <div className="w-[200px] h-9" />
    </header>
  );
}
